/**
 * API Client for communicating with the backend worker
 */

import { settingsStorage } from './settings';

export interface GenerateResponse {
    id: string;
    batchId?: string;
    status: string;
}

export interface StatusResponse {
    videos: Array<{
        id: string;
        status: string;
        videoUrl?: string;
        thumbnailUrl?: string;
    }>;
}

export interface GenerateRequest {
    prompt: string;
    aspectRatio: string;
}

export interface APIError extends Error {
    status?: number;
    code?: string;
}

/**
 * Get the configured backend URL
 */
function getBackendURL(): string {
    // Always use production URL for now to ensure we hit the deployed backend with CORS fixes
    return 'https://veo-banana.croosh.workers.dev';
}

/**
 * Check if an authorization cookie is available
 */
export function hasAuthorizationKey(): boolean {
    const cookie = settingsStorage.getAuthorizationKey();
    return cookie !== null && cookie.length > 0;
}

/**
 * Get authorization headers for API requests
 */
function getAuthorizationHeaders(): Record<string, string> {
    const authCookie = settingsStorage.getAuthorizationKey();

    if (!authCookie) {
        return {};
    }

    // Use Authorization header with Bearer scheme
    return {
        'Authorization': `Bearer ${authCookie}`,
    };
}

/**
 * Create an APIError from a response
 */
function createAPIError(message: string, status?: number, code?: string): APIError {
    const error = new Error(message) as APIError;
    error.status = status;
    error.code = code;
    return error;
}

/**
 * Handle API response errors
 */
async function handleAPIResponse(response: Response): Promise<any> {
    if (response.ok) {
        return response.json();
    }

    // Handle 401 Unauthorized specifically
    if (response.status === 401) {
        throw createAPIError(
            'Authorization required. Please configure your authorization cookie in settings.',
            401,
            'UNAUTHORIZED'
        );
    }

    // Handle other HTTP errors
    let errorMessage = `Request failed: ${response.statusText}`;
    try {
        const errorData = await response.json();
        if (errorData.error) {
            errorMessage = errorData.error;
        }
    } catch {
        // If we can't parse the error response, use the status text
    }

    throw createAPIError(errorMessage, response.status);
}

/**
 * Sleep for a given number of milliseconds
 */
function sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Retry a function with exponential backoff
 */
async function retryWithBackoff<T>(
    fn: () => Promise<T>,
    maxRetries: number = 3,
    initialDelay: number = 1000
): Promise<T> {
    let lastError: Error;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error as Error;

            // Don't retry on the last attempt
            if (attempt < maxRetries - 1) {
                const delay = initialDelay * Math.pow(2, attempt);
                await sleep(delay);
            }
        }
    }

    throw lastError!;
}

/**
 * Generate a video from a prompt
 */
export async function generate(prompt: string, aspectRatio: string): Promise<GenerateResponse> {
    const backendURL = getBackendURL();

    // Validate that we have an authorization cookie before making the request
    if (!hasAuthorizationKey()) {
        throw createAPIError(
            'Authorization cookie required. Please configure your authorization cookie in settings.',
            401,
            'NO_AUTH_COOKIE'
        );
    }

    return retryWithBackoff(async () => {
        const response = await fetch(`${backendURL}/generate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthorizationHeaders(),
            },
            body: JSON.stringify({ prompt, aspectRatio }),
        });

        return handleAPIResponse(response);
    });
}

/**
 * Get the status of all video generations
 */
export async function getStatus(): Promise<StatusResponse> {
    const backendURL = getBackendURL();

    // Validate that we have an authorization cookie before making the request
    if (!hasAuthorizationKey()) {
        throw createAPIError(
            'Authorization cookie required. Please configure your authorization cookie in settings.',
            401,
            'NO_AUTH_COOKIE'
        );
    }

    return retryWithBackoff(async () => {
        const response = await fetch(`${backendURL}/status`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...getAuthorizationHeaders(),
            },
        });

        return handleAPIResponse(response);
    });
}

/**
 * API Client object for convenience
 */
export const apiClient = {
    generate,
    getStatus,
    hasAuthorizationKey,
};
